/* eslint-disable @typescript-eslint/no-explicit-any */
//import libGeneratePDF, { Resolution } from 'react-to-pdf'

import {
  ProformaBaseType,
  GetProformasType,
  AccessoryItemType,
  BlockProformaType,
} from '@customTypes/proforma'
import { ReceiptBlockType } from '@customTypes/block'
import { RawMaterialReceiptType } from '@customTypes/receipt'

import { getBlockResumen } from './block'
import { getReceiptResumen } from './receipt'
import { getRawMaterialResumen } from './rawMaterial'

import { ProformaSeinpro } from '@constants/proforma'
import { periodString } from './string'
import { advancedSearch } from './form'

type SearchParams<T> = {
  params: GetProformasType
  data?: T[]
  limit?: number
}

export const onFilterProforma = <T extends ProformaBaseType>(params: SearchParams<T>) => {
  const {
    params: { field, query },
    data = [],
    limit,
  } = params

  let processedData = data

  if (field === 'date') {
    const { startDate, endDate } = params.params

    if (startDate && endDate) {
      processedData = data.filter(
        (item) => item.createdAt >= startDate && item.createdAt <= endDate,
      )
    } else if (startDate) {
      processedData = data.filter((item) => item.createdAt >= startDate)
    } else if (endDate) {
      processedData = data.filter((item) => item.createdAt <= endDate)
    }
  }

  if (!query || !field || !data) {
    return limit ? data.slice(0, limit) : data
  }

  if (field === 'sequential') {
    processedData = data.filter((item) => item.sequential.toString().includes(query))
  } else if (['name', 'description'].includes(field)) {
    const keyField = field as 'name' | 'description'
    processedData = data.filter((item) =>
      advancedSearch({ search: query.toLowerCase(), label: item[keyField].toLowerCase() }),
    )
  } else if (field === 'client') {
    processedData = data.filter((item) =>
      item.client.name.toLowerCase().includes(query.toLowerCase()),
    )
  } else if (field === 'ruc') {
    processedData = data.filter((item) =>
      item.client.ruc?.toLowerCase().includes(query.toLowerCase()),
    )
  } else if (field === 'status') {
    processedData = data.filter((item) => item.status.toLowerCase().includes(query.toLowerCase()))
  }

  return limit ? processedData.slice(0, limit) : processedData
}

type ProformaDescriptionParams = {
  reduced?: boolean
  index?: number
  item: ProformaBaseType['items'][0]
  userRole: string
  handleInput?: (path: string, value: number | string) => void
}

export const getDescription = (params: ProformaDescriptionParams): JSX.Element | string => {
  const { item, reduced, handleInput, userRole, index } = params
  if (item.type === 'raw-material') {
    return getRawMaterialResumen({
      rawMaterial: item as RawMaterialReceiptType,
      handleInput,
      userRole,
      reduced,
      index,
    })
  } else if (item.type === 'receipt') {
    return getReceiptResumen({
      receipt: item as ReceiptBlockType,
      handleInput,
      userRole,
      reduced,
      index,
    })
  } else if (item.type === 'block') {
    return getBlockResumen({
      block: item as BlockProformaType,
      handleInput,
      userRole,
      reduced,
      index,
    })
  } else if (item.type === 'accessory') {
    return periodString((item as AccessoryItemType).description)
  }
  return ''
}

type ProformaTotalTypeParams = {
  item: ProformaBaseType['items'][0]
  unit?: boolean
  itemsProforma: ProformaBaseType['items']
}

export const getProformaTotal = (params: ProformaTotalTypeParams): number => {
  const { item, itemsProforma, unit } = params
  if (!item) return 0

  if (item.type === 'raw-material') {
    const rmCast = item as RawMaterialReceiptType
    return rmCast.value * (unit ? 1 : rmCast.quantity)
  } else if (item.type === 'receipt') {
    const receiptCast = item as ReceiptBlockType
    return (
      receiptCast.rawMaterials.reduce((acc, rm) => acc + rm.value * rm.quantity, 0) *
      (unit ? 1 : receiptCast.quantity)
    )
  } else if (item.type === 'block') {
    const blockCast = item as BlockProformaType

    if (blockCast.receipts.length > 0) {
      return (
        blockCast.receipts.reduce(
          (acc, re) =>
            acc +
            re.rawMaterials.reduce((acc, rm) => acc + rm.value * rm.quantity, 0) * re.quantity,
          0,
        ) * (unit ? 1 : blockCast.quantity)
      )
    } else {
      return (blockCast.value || 0) * (unit ? 1 : blockCast.quantity)
    }
  } else if (item.type === 'accessory') {
    const accCast = item as AccessoryItemType
    const linkedItem = itemsProforma.find((i) => i.uid === accCast.uidItem)

    if (!linkedItem) return 0

    return (
      (accCast.quantity / 100) *
      getProformaTotal({
        item: linkedItem,
        itemsProforma,
        unit,
      })
    )
  }

  return 0
}

// export const generatePDF = async (ref: any, filename: string) => {
//   try {
//     await libGeneratePDF(ref, {
//       filename: cleanProformaName(filename),
//       method: 'open',
//       resolution: Resolution.LOW,
//       page: {
//         format: 'A4',
//         orientation: 'portrait',
//       },
//     })

//     notifications.show({
//       color: 'green',
//       title: 'PDF Generado',
//       message: 'PDF generado correctamente',
//     })
//   } catch (error) {
//     Logger.error('Error generating PDF', error)
//     notifications.show({
//       color: 'red',
//       title: 'Error',
//       message: 'Error generando PDF',
//     })
//   }
// }

export const getSequentialStr = (seq: number) => {
  const { sequential } = ProformaSeinpro

  const seqStr = seq.toString()

  return sequential.substring(0, sequential.length - seqStr.length) + seqStr
}

type GetTotalsParams = {
  items: ProformaBaseType['items']
  discount: number
  iva: number
}

export const getTotals = (params: GetTotalsParams) => {
  const { items, discount, iva } = params
  let subtotal = 0

  for (const item of items) {
    subtotal += getProformaTotal({
      item,
      itemsProforma: items,
    })
  }

  const discountValue = subtotal * (discount / 100)

  const netTotal = subtotal - discountValue

  const ivaValue = netTotal * (iva / 100)

  return {
    subtotal,
    netTotal,
    iva: ivaValue,
    discount: discountValue,
    total: netTotal + ivaValue,
  }
}
